import React, { useState, useEffect } from 'react';
import { 
  LayoutDashboard, FileText, Briefcase, Clock, LogOut, 
  Bell, CheckCircle, Search, Plus, RefreshCw, ArrowRight 
} from 'lucide-react';
import { api, getMediaUrl, LoadingSpinner, Badge, Modal } from '../utils';

// --- Sub-Components ---
const StatCard = ({ label, value, icon: Icon, color }) => (
  <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100 flex items-center gap-4 hover:translate-y-[-4px] transition-all">
    <div className={`p-3 rounded-2xl bg-${color}-50 text-${color}-600`}><Icon size={24} /></div>
    <div>
      <p className="text-sm text-gray-400 font-medium">{label}</p>
      <p className="text-2xl font-black text-gray-900">{value || 0}</p>
    </div>
  </div>
);

const DashboardHome = ({ data, ads, setActiveTab }) => (
  <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
      <StatCard label="Total Orders" value={data?.summary?.total_orders} icon={Briefcase} color="indigo" />
      <StatCard label="Pending" value={data?.summary?.pending} icon={Clock} color="amber" />
      <StatCard label="Ready" value={data?.summary?.ready} icon={CheckCircle} color="green" />
      <StatCard label="Delivered" value={data?.summary?.delivered} icon={CheckCircle} color="emerald" />
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      <div className="lg:col-span-2 bg-white rounded-3xl p-6 shadow-sm border border-gray-100">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-bold text-gray-900">Recent Orders</h3>
          <button onClick={() => setActiveTab('orders')} className="text-sm text-indigo-600 font-semibold">View all</button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="text-left text-xs text-gray-400 uppercase tracking-wider border-b border-gray-50">
              <tr><th className="pb-4">Service</th><th className="pb-4">Status</th><th className="pb-4">Amount</th><th className="pb-4 text-right">Date</th></tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {data?.recent_orders?.map(order => (
                <tr key={order.id} className="hover:bg-gray-50">
                  <td className="py-4 font-medium">{order.service?.name}</td>
                  <td className="py-4"><Badge status={order.status}>{order.status}</Badge></td>
                  <td className="py-4 font-semibold">?{order.amount}</td>
                  <td className="py-4 text-right text-sm text-gray-500">{new Date(order.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
              {data?.recent_orders?.length === 0 && (<tr><td colSpan="4" className="py-12 text-center text-gray-400">No orders found</td></tr>)}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white rounded-3xl p-6 shadow-sm border border-gray-100 overflow-hidden relative">
        <h3 className="text-lg font-bold text-gray-900 mb-6">Announcements</h3>
        <div className="space-y-4">
          {ads.length > 0 ? (
            <div className="relative rounded-2xl overflow-hidden h-48 group">
              <img src={getMediaUrl(ads[0].file_url)} alt={ads[0].title} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-black/40 p-4 flex flex-col justify-end"><p className="text-white text-sm font-bold">{ads[0].title}</p></div>
            </div>
          ) : <p className="text-gray-400 text-center py-8">No current ads</p>}
        </div>
      </div>
    </div>
  </div>
);

const ServicesBrowser = ({ token }) => {
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCat, setSelectedCat] = useState(null);
  const [search, setSearch] = useState('');
  const [selectedSvc, setSelectedSvc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    const fetchServices = async () => {
      setLoading(true);
      try {
        const cats = await api.get('/public/service-categories');
        setCategories(cats);
        const svcs = await api.get(`/public/services?${selectedCat ? `category_id=${selectedCat}` : ''}&q=${search}`);
        setServices(svcs);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetchServices();
  }, [selectedCat, search]);

  const handleApply = async () => {
    if (!selectedSvc) return;
    setApplying(true);
    try {
      await api.post('/customer/apply-service', { service_id: selectedSvc.id }, token);
      alert('Service applied successfully!');
      setSelectedSvc(null);
    } catch (err) { alert(err.detail || 'Failed to apply'); }
    finally { setApplying(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row gap-4 justify-between">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..." className="w-full pl-12 pr-4 py-3 border rounded-2xl outline-none" />
        </div>
        <div className="flex gap-2 overflow-x-auto pb-2">
          <button onClick={() => setSelectedCat(null)} className={`px-4 py-2 rounded-xl text-sm font-bold ${!selectedCat ? 'bg-indigo-600 text-white' : 'bg-white'}`}>All</button>
          {categories.map(cat => (
            <button key={cat.id} onClick={() => setSelectedCat(cat.id)} className={`px-4 py-2 rounded-xl text-sm font-bold ${selectedCat === cat.id ? 'bg-indigo-600 text-white' : 'bg-white'}`}>{cat.name}</button>
          ))}
        </div>
      </div>

      {loading ? <LoadingSpinner /> : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {services.map(svc => (
            <div key={svc.id} className="bg-white p-6 rounded-3xl border">
              <div className="w-12 h-12 bg-indigo-50 text-indigo-600 rounded-2xl flex items-center justify-center mb-4">
                {svc.logo ? <img src={getMediaUrl(svc.logo)} alt={svc.name} className="w-8 h-8" /> : <Briefcase size={24} />}
              </div>
              <h3 className="text-lg font-bold">{svc.name}</h3>
              <p className="text-gray-400 text-sm mt-2 line-clamp-2 h-10">{svc.description}</p>
              <div className="mt-6 flex justify-between items-center">
                <div><p className="text-xs text-gray-400">Starts from</p><p className="text-xl font-black text-indigo-600">?{svc.base_price}</p></div>
                <button onClick={() => setSelectedSvc(svc)} className="p-3 bg-gray-50 rounded-xl hover:bg-indigo-600 hover:text-white"><Plus size={20} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {selectedSvc && (
        <Modal isOpen={true} onClose={() => setSelectedSvc(null)} title="Service Details">
          <div className="space-y-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-indigo-50 text-indigo-600 rounded-2xl flex items-center justify-center">
                {selectedSvc.logo ? <img src={getMediaUrl(selectedSvc.logo)} className="w-10 h-10" /> : <Briefcase size={32} />}
              </div>
              <div><h4 className="text-xl font-bold">{selectedSvc.name}</h4><p className="text-indigo-600 font-bold">?{selectedSvc.base_price}</p></div>
            </div>
            <p className="text-sm bg-slate-50 p-4 rounded-2xl">{selectedSvc.description}</p>
            <button onClick={handleApply} disabled={applying} className="w-full py-4 bg-indigo-600 text-white font-bold rounded-xl flex justify-center gap-2">
              {applying ? <RefreshCw className="animate-spin" /> : 'Apply for this Service'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

const CustomerOrders = ({ token }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/customer/orders', token).then(setOrders).catch(console.error).finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-6">
      {loading ? <LoadingSpinner /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {orders.map(order => (
            <div key={order.id} className="bg-white rounded-3xl p-6 border">
              <div className="flex justify-between items-start mb-6">
                <div><p className="text-xs text-gray-400 font-semibold mb-1">Order #{order.id}</p><h3 className="text-lg font-bold">{order.service?.name}</h3></div>
                <Badge status={order.status}>{order.status}</Badge>
              </div>
              <div className="flex items-center gap-8 mb-6">
                <div><p className="text-xs text-gray-400 font-medium mb-1">Fee</p><p className="text-lg font-black">?{order.amount}</p></div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const CustomerDocuments = ({ token }) => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/customer/documents', token).then(setDocs).catch(console.error).finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-6">
      {loading ? <LoadingSpinner /> : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {docs.map(doc => (
            <div key={doc.id} className="bg-white p-6 rounded-3xl border">
              <div className="w-12 h-12 bg-indigo-50 text-indigo-600 rounded-2xl flex items-center justify-center mb-4"><FileText size={24} /></div>
              <h4 className="text-sm font-bold mb-4">{doc.title}</h4>
              <div className="flex gap-2">
                <a href={getMediaUrl(doc.file_path)} target="_blank" rel="noreferrer" className="flex-1 py-2 bg-indigo-50 text-indigo-600 text-center text-xs font-bold rounded-xl">View</a>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// --- Main Export ---
export default function CustomerDashboard({ token, onLogout }) {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ads, setAds] = useState([]);

  const fetchData = async () => {
    try {
      const dashboard = await api.get('/customer/dashboard', token);
      setData(dashboard);
      const adsList = await api.get('/public/ads');
      setAds(adsList);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, []);

  const SidebarItem = ({ id, icon: Icon, label }) => (
    <button onClick={() => setActiveTab(id)} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-medium ${activeTab === id ? 'bg-indigo-600 text-white' : 'text-gray-500'}`}>
      <Icon size={20} /> <span className="hidden lg:inline">{label}</span>
    </button>
  );

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <aside className="w-20 lg:w-64 bg-white border-r flex flex-col p-4 fixed h-full z-30">
        <div className="flex items-center gap-2 mb-10 px-2"><div className="bg-indigo-600 p-2 rounded-lg"><Briefcase className="text-white" size={20} /></div><span className="text-xl font-bold hidden lg:inline">Connect</span></div>
        <nav className="flex-1 space-y-2">
          <SidebarItem id="dashboard" icon={LayoutDashboard} label="Dashboard" />
          <SidebarItem id="services" icon={Briefcase} label="Services" />
          <SidebarItem id="orders" icon={Clock} label="My Orders" />
          <SidebarItem id="documents" icon={FileText} label="My Documents" />
        </nav>
        <div className="pt-4 border-t">
          <button onClick={onLogout} className="w-full flex items-center gap-3 px-4 py-3 text-red-600 rounded-xl font-medium"><LogOut size={20} /> <span className="hidden lg:inline">Logout</span></button>
        </div>
      </aside>

      <main className="flex-1 ml-20 lg:ml-64 p-4 lg:p-8">
        <header className="flex justify-between items-center mb-8">
          <div><h1 className="text-2xl font-bold capitalize">{activeTab}</h1><p className="text-gray-500 text-sm">Welcome, {data?.customer?.name}</p></div>
          <div className="flex items-center gap-4">
            <button className="p-2 bg-white rounded-full border text-gray-500"><Bell size={20} /></button>
            <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-full border">
              <div className="w-8 h-8 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center font-bold">{data?.customer?.name?.charAt(0) || 'U'}</div>
            </div>
          </div>
        </header>
        {loading ? <LoadingSpinner /> : (
          <>
            {activeTab === 'dashboard' && <DashboardHome data={data} ads={ads} setActiveTab={setActiveTab} />}
            {activeTab === 'services' && <ServicesBrowser token={token} />}
            {activeTab === 'orders' && <CustomerOrders token={token} />}
            {activeTab === 'documents' && <CustomerDocuments token={token} />}
          </>
        )}
      </main>
    </div>
  );
}
